import { MonitorCog } from 'lucide-react'
import type { ReactNode } from 'react'

import type { TeamMemberSummary } from '@aeris/contracts'
import {
  Avatar,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
} from '@aeris/ui'

import { formatDate } from '../lib/date-format.ts'

import { ROLE_LABELS } from './role-labels.ts'

/**
 * A ficha de quem está na equipe — F154.
 *
 * Esta é a mais modesta das cinco fichas, e vale dizer por quê: a tabela de
 * Equipe já mostra quase tudo que a pessoa tem. O que ela acrescenta são duas
 * coisas que a tela nunca respondeu:
 *
 * - **a situação da CONTA**, que é pergunta diferente da situação do acesso a
 *   esta loja. "Não trabalha mais aqui" e "não consegue entrar em lugar
 *   nenhum" são estados distintos, e o segundo explica o chamado de quem jura
 *   que a senha está certa;
 * - **desde quando está na equipe**, que é o que se procura quando se confere
 *   comissão ou tempo de casa.
 *
 * ## Por que só UMA ação no rodapé
 *
 * O ⋯ da linha tem cinco (Telas, Comissão, Sessões, Redefinir senha, Remover
 * acesso), cada uma com a sua permissão e a sua confirmação. Copiá-las para cá
 * seria manter as cinco regras em dois lugares — e descobrir a divergência no
 * dia em que só um dos dois for corrigido. A ficha oferece a mesma ação
 * principal que a linha oferece, e quem decide se ela existe é a TELA.
 */

export const TeamMemberDialog = ({
  member,
  commissionLabel,
  onClose,
  onPages,
}: {
  member: TeamMemberSummary
  /** `undefined` quando quem lê não tem `commission.manage`. */
  commissionLabel?: string | undefined
  onClose: () => void
  /** Só quem pode agir sobre esta pessoa. Ausente, o botão não existe. */
  onPages?: (() => void) | undefined
}): ReactNode => (
  <Dialog open onOpenChange={onClose}>
    <DialogContent
      title={member.displayName}
      description="Conferir a pessoa. Nada muda nesta tela."
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <Avatar label={member.displayName} seed={member.userId} size="md" />
          <div className="min-w-0">
            <p className="truncate text-(--text-primary)">{member.email}</p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <Badge tone="info">{ROLE_LABELS[member.role]}</Badge>
              {member.status === 'disabled' ? (
                <Badge tone="neutral">Sem acesso a esta loja</Badge>
              ) : null}
              {/*
                A conta em si, e não o acesso a esta loja. É a distinção que o
                contrato faz questão de manter separada, e que a tabela não
                mostrava em lugar nenhum.
              */}
              {member.accountStatus === 'disabled' ? (
                <Badge tone="danger">Conta desativada</Badge>
              ) : null}
              {member.mustChangePassword ? (
                <Badge tone="warning">Ainda com a senha temporária</Badge>
              ) : null}
            </div>
          </div>
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-(length:--text-sm)">
          <dt className="text-(--text-muted)">Na equipe desde</dt>
          <dd className="text-(--text-primary)">
            {formatDate(member.createdAt)}
          </dd>
          <dt className="text-(--text-muted)">Último acesso</dt>
          <dd className="text-(--text-primary)">
            {member.lastSeenAt === null
              ? 'nunca entrou'
              : formatDate(member.lastSeenAt)}
          </dd>
          {commissionLabel === undefined ? null : (
            <>
              <dt className="text-(--text-muted)">Comissão</dt>
              <dd className="text-(--text-primary)">{commissionLabel}</dd>
            </>
          )}
        </dl>
      </div>

      <DialogFooter>
        <Button variant="secondary" onClick={onClose}>
          Fechar
        </Button>
        {onPages === undefined ? null : (
          <Button
            variant="primary"
            leadingIcon={<MonitorCog aria-hidden="true" className="h-4 w-4" />}
            onClick={onPages}
          >
            Telas
          </Button>
        )}
      </DialogFooter>
    </DialogContent>
  </Dialog>
)
